/**
 * Treyler mənbəyinin həlli və HLS yükləmə.
 *
 * ⚠ TREYLER PUBLİKDİR — nə entitlement, nə DRM, nə playback ticket tələb edir; ona
 * görə birbaşa native `<video>`-da oynayır. Tam film isə `POST /api/playback/ticket`
 * biletini alır və (DRM olsa) Shaka ilə oynayır — bax `screens/player.ts` + `lib/shaka.ts`.
 *
 * ⚠ NİYƏ NATIVE `<video>`, MUX-PLAYER DEYİL. Müasir web komponentləri TV
 * mühərriklərində (Chrome 53) ümumiyyətlə işləmir — bax `Docs/KINOBOX_PLAYER.md` §2.1.
 *
 * ⚠ NATIVE HLS ARTIQ BİRİNCİ SEÇİM DEYİL (2026-08-05 auditi). Tizen və webOS
 * HLS-i doğma `<video>`-da oynadır və uzun müddət «TV-də kitabxana lazım deyil»
 * qərarı verilmişdi. Nəticə: REAL TELEVİZORDA keyfiyyət/altyazı/səs menyusu ÖLÜ
 * idi (native yolda nə hls.js, nə Shaka obyekti var, yəni pillə siyahısı da,
 * altyazı yolu da bizim əlimizdə deyil) və başlanğıc bant genişliyi təxmini
 * (KINOBOX_PLAYER.md K2) tətbiq olunmurdu. İndi sıra tərsinədir: MSE varsa
 * hls.js, native isə EHTİYAT — bax `hasMediaSource()` və `screens/player.ts →
 * playHlsUrl`. DRM yolu (Shaka/EME) bu qərardan TƏSİRLƏNMİR.
 */

import { httpJson } from '@/lib/compat'

/** Treyler mənbəyi — oynadıla bilən HLS və ya «dəstəklənmir» + səbəb. */
export type TrailerSource =
  | { kind: 'hls'; url: string; poster: string | null }
  | { kind: 'unsupported'; reason: string }

/**
 * `mux:{id}` / `vimeo:{id}` → oynadıla bilən mənbə.
 *
 * ⚠ Vimeo `<video>`-da native oynanmır (cross-origin iframe + postMessage
 * tələb edir) və paketlənmiş TV app-ının CSP-si iframe-ə etibar etmir. Ona görə
 * vimeo treyleri hələ «dəstəklənmir» kimi qeyd olunur — uydurma oynatma yoxdur.
 */
export function resolveTrailer(asset: string | null | undefined): TrailerSource | null {
  if (!asset) return null

  const sep = asset.indexOf(':')
  const provider = sep >= 0 ? asset.slice(0, sep) : 'mux'
  const id = sep >= 0 ? asset.slice(sep + 1) : asset
  if (!id) return null

  if (provider === 'mux') {
    return {
      kind: 'hls',
      url: 'https://stream.mux.com/' + id + '.m3u8',
      poster: 'https://image.mux.com/' + id + '/thumbnail.webp?width=1920',
    }
  }

  if (provider === 'vimeo') {
    // Sinxron həll YOX — Vimeo serverdə açıq HLS-ə çevrilir (`resolveVimeoTrailer`).
    // Birbaşa embed videonun məxfilik parametrləri + TV CSP-si ucbatından oynamır.
    return { kind: 'unsupported', reason: 'Vimeo treyleri hazırlanır…' }
  }

  return { kind: 'unsupported', reason: 'Naməlum treyler mənbəyi.' }
}

/**
 * Vimeo treylerini SERVER açıq HLS-ə həll edir (`GET /api/catalog/content/{slug}/trailer`).
 * Backend Vimeo API-dən imzalı, açıq (CORS *) HLS gətirir; real TV onu native `<video>`-da
 * oynadır (mux ilə eyni yol). Birbaşa Vimeo embed-i «privacy settings» xətası verir. Yoxdursa null.
 */
/**
 * SLUG → treyler mənbəyi KEŞİ (ORTAQ).
 *
 * ⚠⚠ NİYƏ ORTAQ (sahib tələbi 2026-08-24: «vebtvdə modal açılan kimi poster
 *    görünmədən trailer başlamalıdır»).
 *
 *    ÖLÇÜLDÜ (tv.kinobox.az, kart klikindən sonra `PerformanceResourceTiming`):
 *        /api/catalog/content/{slug}/trailer   4 → 589 ms   (585 ms)
 *        manifest                            592 → 1529 ms
 *        ilk seqmentlər                     1532 → 2009 ms
 *        ilk kadr                                 ~3.0 san
 *    Yəni gecikmənin BİRİNCİ altıda biri modalın mənbəni SORUŞMASIDIR — halbuki
 *    kart öngörünümü (`ui/cardPreview.ts`) eyni ucu fokusdan 1 saniyə sonra
 *    ONSUZ DA çağırırdı və cavabı ÖZ içində saxlayırdı. İki keş vardı, biri
 *    digərini görmürdü: istifadəçi kartın üstündə dayanıb sonra açanda hazır
 *    cavab TƏKRAR istənirdi. İndi keş burada — hər iki tərəf eyni sətri oxuyur.
 * ⚠ Yalnız uğurlu mənbə keşlənir: resolver boş nəticə ilə şəbəkə xətasını
 *   ayırmadığı üçün boş nəticə sonrakı sağlam cəhdi bloklamamalıdır.
 * ⚠ TTL — imzalı Vimeo/Mux ünvanları müddətlidir; `cardPreview`-dəki köhnə
 *   rəqəm (5 dəqiqə) saxlanıldı.
 */
const TRAILER_TTL_MS = 300000

interface CachedTrailer {
  source: TrailerSource | null
  at: number
}

const trailerCache: Record<string, CachedTrailer> = {}

/**
 * Keşli mənbə həlli — `resolveVimeoTrailer`-in üstündəki yeganə giriş nöqtəsi.
 * Kart öngörünümü də, modal da, hero da bunu çağırır.
 */
export function trailerSourceFor(slug: string): Promise<TrailerSource | null> {
  const cached = trailerCache[slug]
  if (cached && Date.now() - cached.at < TRAILER_TTL_MS) return Promise.resolve(cached.source)
  return resolveVimeoTrailer(slug).then(function (source) {
    // 2026-10-11: offline cavabından sonrakı uğurlu cəhd 5 dəqiqə bloklanırdı.
    if (source) trailerCache[slug] = { source, at: Date.now() }
    return source
  })
}

export function resolveVimeoTrailer(slug: string): Promise<TrailerSource | null> {
  return httpJson<{ manifestUrl?: string }>(
    '/api/catalog/content/' + encodeURIComponent(slug) + '/trailer',
  )
    .then((response) =>
      response && response.manifestUrl
        ? ({ kind: 'hls', url: response.manifestUrl, poster: null } as TrailerSource)
        : null,
    )
    .catch(() => null)
}

/**
 * MSE (MediaSource Extensions) mövcuddurmu — hls.js-in İŞLƏYƏ BİLMƏSİNİN yeganə şərti.
 *
 * ⚠ NİYƏ AYRICA YOXLANILIR, `Hls.isSupported()` KİFAYƏT ETMİR: `isSupported()`
 *   yalnız kitabxana YÜKLƏNDİKDƏN sonra cavab verə bilir. MSE-siz köhnə mühərrikdə
 *   bu, əvvəlcə CDN-dən yüz kilobaytlıq skriptin çəkilib parse olunması, sonra isə
 *   «dəstəklənmir» cavabı deməkdir — TV-də bu, filmin açılışına əlavə gecikmədir.
 *   Bu yoxlama həmin yolu heç başlamadan kəsir.
 * ⚠ Kodek sətri hls.js-in öz `isSupported()` yoxlaması ilə EYNİDİR (H.264 + AAC,
 *   sonra yalnız-video variantı) — biz kitabxananın qərarını təxmin etmirik,
 *   onun şərtini təkrarlayırıq.
 * ⚠ `WebKitMediaSource` da yoxlanılır: bəzi köhnə Tizen/webOS mühərriklərində MSE
 *   yalnız prefiksli qlobal kimi mövcuddur.
 */
export function hasMediaSource(): boolean {
  const host = window as unknown as {
    MediaSource?: { isTypeSupported?: (type: string) => boolean }
    WebKitMediaSource?: unknown
  }
  const source = host.MediaSource
  if (source && typeof source.isTypeSupported === 'function') {
    return (
      source.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"') ||
      source.isTypeSupported('video/mp4; codecs="avc1.42E01E"')
    )
  }
  return Boolean(host.WebKitMediaSource)
}

/** Doğma HLS dəstəyi (Tizen · webOS · Safari «probably»/«maybe» qaytarır). */
export function canPlayHlsNatively(video: HTMLVideoElement): boolean {
  // ⚠ `'maybe'` QƏBUL EDİLMİR (2026-08-03-də brauzerdə ölçüldü — REAL DEFEKT).
  //   Chrome/Edge `canPlayType('application/vnd.apple.mpegurl')` üçün **'maybe'**
  //   qaytarır, halbuki HLS-i ÜMUMİYYƏTLƏ oynada bilmir. Spesifikasiyada 'maybe'
  //   «faylı çəkmədən deyə bilmərəm» deməkdir — «bəli» yox.
  //   Nəticə: Vimeo filmlərinin (kataloqda 66 ədəd) manifesti birbaşa `video.src`-ə
  //   yazılırdı → `MediaError.code = 4` (SRC_NOT_SUPPORTED) → ekranda «Video
  //   mənbəyinə çatmaq alınmadı». Mux filmləri DRM/Shaka yolu ilə getdiyi üçün
  //   işləyirdi — istifadəçinin «BƏZİ filmlər xəta verir» müşahidəsi məhz budur.
  //   Safari 'probably' qaytarır və doğrudan da oynadır.
  return video.canPlayType('application/vnd.apple.mpegurl') === 'probably'
}

/** hls.js-in minimal tip səthi — yalnız işlətdiyimiz üzvlər. */
/** hls.js rendition-u — yalnız menyuda göstərdiyimiz sahələr. */
export interface HlsLevel {
  height?: number
  width?: number
  bitrate?: number
}

/** hls.js altyazı/audio yolu — menyu üçün minimal forma. */
export interface HlsTrack {
  id?: number
  name?: string
  lang?: string
}

export interface HlsLike {
  loadSource(url: string): void
  attachMedia(video: HTMLVideoElement): void
  /**
   * ⚠ `type` DƏ OXUNUR (547): hls.js xətanı kateqoriya ilə verir
   *   (`networkError` · `mediaError` · `otherError`) və player ona görə fərqli
   *   izah göstərir — Android TV-dəki `playbackErrorMessage` cədvəlinin qarşılığı.
   */
  on(event: string, cb: (event: string, data: { fatal?: boolean; type?: string }) => void): void
  destroy(): void
  /**
   * BƏRPA ÇAĞIRIŞLARI (2026-09-26, oynadıcı ölümcül xətada əvvəl dərhal mesaj verirdi):
   * şəbəkə xətasında `startLoad()` mövqedən davam edir, media xətasında
   * `recoverMediaError()` MSE-ni yenidən qurur, ikinci dəfə `swapAudioCodec()` ilə.
   * Opsional: kitabxana CDN-dən gəlir, köhnə nüsxədə ola bilməz — çağıran yoxlayır.
   */
  startLoad?(startPosition?: number): void
  recoverMediaError?(): void
  swapAudioCodec?(): void
  /**
   * ⚠ AŞAĞIDAKI SAHƏLƏR OPSİONALDIR, çünki kitabxana CDN-dən gəlir və köhnə
   *   nüsxədə ola bilməz. Player menyusu (keyfiyyət · altyazı · səs) hər birini
   *   ayrıca yoxlayır və yoxdursa BÖLMƏNİ ÜMUMİYYƏTLƏ GÖSTƏRMİR — boş menyu
   *   «sınıq app» kimi oxunur.
   */
  levels?: HlsLevel[]
  /**
   * OXUYANDA — HAZIRDA OYNAYAN pillənin indeksi (avtomatik rejimdə də həqiqi
   * pillə, −1 YOX). YAZANDA — −1 avtomatikə (ABR) qaytarır, ≥0 pilləni kilidləyir.
   *
   * ⚠ BU ASİMMETRİYA REAL QÜSURA SƏBƏB OLMUŞDU: kod `currentLevel < 0`-ı
   *   «avtomatik» kimi oxuyurdu, ona görə ABR işləyəndə paneldə «Avtomatik»
   *   sətri heç vaxt nişanlanmır, əvəzinə konkret pillə seçili görünürdü.
   *   Rejimi `autoLevelEnabled` deyir.
   */
  currentLevel?: number
  /** ABR açıqdırmı — «Avtomatik» sətrinin nişanı buna görə qoyulur. */
  autoLevelEnabled?: boolean
  /**
   * Televizor dekoderinin həddini aşan pilləni çıxarmaq üçün (2026-10-24, `lib/videoCap.ts`).
   * Opsional: köhnə nüsxədə yoxdursa ehtiyat `autoLevelCapping`-dir.
   */
  removeLevel?(index: number): void
  /** ABR-ın seçə biləcəyi ən yüksək pillə indeksi (−1 = tavan yoxdur). */
  autoLevelCapping?: number
  subtitleTracks?: HlsTrack[]
  /** −1 = söndürülüb. */
  subtitleTrack?: number
  audioTracks?: HlsTrack[]
  audioTrack?: number
}
/**
 * hls.js konfiqurasiyası — YALNIZ işlətdiyimiz açarlar yazılıb (tam tip
 * kitabxananın özündədir, biz onu CDN-dən götürürük və tip gətirmirik).
 */
export interface HlsConfig {
  /** Başlanğıc bant genişliyi təxmini (bit/s) — ilk seqmentin keyfiyyətini seçir. */
  abrEwmaDefaultEstimate?: number
  /** Bufer hədəfi (saniyə). */
  maxBufferLength?: number
}

interface HlsCtor {
  new (config?: HlsConfig): HlsLike
  isSupported(): boolean
  /**
   * ⚠ `LEVEL_SWITCHED` OPSİONALDIR: kitabxana CDN-dən gəlir və köhnə nüsxədə
   *   hadisə adı olmaya bilər. `hls.on(undefined, …)` çağırışı kitabxananın
   *   içində çökərdi, ona görə çağıran əvvəlcə adın varlığını yoxlayır.
   */
  Events: { ERROR: string; MANIFEST_PARSED: string; LEVEL_SWITCHED?: string }
  /** Xəta kateqoriyaları — `data.type` ilə müqayisə üçün (bərpa yolu, 2026-09-26). */
  ErrorTypes?: { NETWORK_ERROR: string; MEDIA_ERROR: string }
}

/**
 * ⚠ VERSİYA DƏQİQ BAĞLIDIR (əvvəl üzən `@1` idi): kitabxana artıq TV-də treylerin,
 *   DRM-siz filmin VƏ canlı yayımın qapısıdır — yeni 1.x buraxılışı bir gecədə
 *   kodumuza toxunmadan televizoru sındıra bilərdi. Shaka da eyni cür bağlıdır
 *   (`lib/shaka.ts` → 4.16.42).
 */
const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.20/dist/hls.min.js'

/**
 * Skript cavab verməsə nə qədər gözlənilir (ms).
 *
 * ⚠ NİYƏ TAYMER LAZIMDIR: `script.onerror` YALNIZ sorğu RƏDD ediləndə gəlir.
 *   Sorğu «qara dəliyə» düşəndə (operator DNS-i cavabsız qalır, Tizen `<access>`
 *   siyahısı sorğunu sükutla udur) nə `onload`, nə `onerror` gəlir — söz heç vaxt
 *   həll olunmur və ekran «Video yüklənir…»-də İLİŞİR. Taymer bitəndə `null`
 *   qaytarılır və çağıran native `<video>` yoluna düşür (menyusuz, amma işlək).
 */
const SCRIPT_TIMEOUT_MS = 8000

let hlsPromise: Promise<HlsCtor | null> | null = null

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SƏSSİZ FON TREYLERİ — verilmiş `<video>` elementində
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠ NİYƏ BURADADIR, EKRANIN İÇİNDƏ DEYİL: eyni davranış artıq İKİ yerdə lazımdır —
 *   film səhifəsində (`screens/detail.ts`) və film modalında (`ui/detailModal.ts`).
 *   Modal üçün ikinci nüsxə yazmaq həmin qaydaların (avtoplay blokunun aşkarı,
 *   sığorta saatı, hls.js konfiqurasiyası) bir yerdə düzəlib o birində unudulması
 *   deməkdir — bu fayldakı iki keçmiş qüsur məhz belə yaranmışdı.
 * ⚠ `screens/detail.ts` QƏSDƏN TOXUNULMADI: o ekran dərin keçid/ehtiyat yoludur və
 *   işləkdir; onu bu funksiyaya köçürmək davranışını dəyişmək riski daşıyır.
 *
 * ⚠ SƏSSİZDİR VƏ DÖVR ETMİR (telefon/iOS ilə eyni qayda): TV salonda işləyir, səsli
 *   treyler ayrıca düymədədir; bitəndə kadr afişaya qayıdır (`onFallback`).
 * ⚠ AVTOPLAY BLOKU AŞKAR EDİLİR: TV brauzerlərinin bir hissəsi səssiz videoya da
 *   icazə vermir. `play()` rəddi udulsaydı ekranda DONMUŞ İLK KADR qalardı
 *   (`loadeddata` yenə atılır) — ona görə iki yoxlama var: promise rəddi və
 *   1.5 saniyədən sonra `paused` yoxlaması (köhnə mühərrikdə promise yoxdur).
 *
 * @param onFirstFrame ilk kadr hazırdır — çağıran videonu görünən edir.
 * @param onFallback treyler yoxdur / alınmadı / bloklandı / bitdi — afişa qalsın.
 * @returns dayandırma funksiyası. Ekran söküləndə və ya məzmun dəyişəndə MÜTLƏQ
 *   çağırılmalıdır: iki video paralel dekod olunanda köhnə TV donur.
 */
let activeTrailerStop: (() => void) | null = null
const trailerStopListeners: Array<(video: HTMLVideoElement, time: number) => void> = []

/** 2026-10-11: hero + modal testində iki video paralel qalırdı.
 * Yeni treyler köhnə hazırlığı da ləğv edir; gecikmiş manifest onu diriltmir. */
export function stopActiveTrailer(): void {
  if (activeTrailerStop) activeTrailerStop()
}

export function startMutedTrailer(
  video: HTMLVideoElement,
  asset: string | null | undefined,
  slug: string,
  onFirstFrame: () => void,
  onFallback: () => void,
): () => void {
  stopActiveTrailer()
  let disposed = false
  let hls: HlsLike | null = null
  let frameTimer = 0
  let probeTimer = 0

  const clearTimers = (): void => {
    if (frameTimer) {
      window.clearTimeout(frameTimer)
      frameTimer = 0
    }
    if (probeTimer) {
      window.clearTimeout(probeTimer)
      probeTimer = 0
    }
  }

  const ready = (): void => {
    clearTimers()
    if (!disposed) onFirstFrame()
  }
  const giveUp = (): void => {
    clearTimers()
    if (!disposed) onFallback()
  }

  // ⚠ İSTİNADLAR SAXLANILIR: element TƏKRAR İŞLƏDİLİR (modal DOM-u bir dəfə
  //   qurulur — PERFORMANCE.md), yəni dinləyicilər dayandırmada GÖTÜRÜLMƏLİDİR.
  //   Əks halda hər açılışda bir dəstə dinləyici üst-üstə yığılardı.
  const onLoaded = (): void => ready()
  const onPlaying = (): void => ready()
  const onEnded = (): void => giveUp()
  const onError = (): void => giveUp()

  const stop = (): void => {
    if (disposed) return
    disposed = true
    const wasActive = activeTrailerStop === stop
    const stoppedAt = video.currentTime
    if (wasActive) activeTrailerStop = null
    clearTimers()
    video.removeEventListener('loadeddata', onLoaded, false)
    video.removeEventListener('playing', onPlaying, false)
    video.removeEventListener('ended', onEnded, false)
    video.removeEventListener('error', onError, false)
    video.pause()
    if (hls) {
      hls.destroy()
      hls = null
    }
    video.removeAttribute('src')
    // `load()` olmadan bəzi TV-lərdə dekoder buraxılmır və növbəti oynatma
    // «no free decoder» ilə uğursuz olur (player.ts-dəki eyni qeyd).
    video.load()
    onFallback()
    if (wasActive) trailerStopListeners.slice().forEach(listener => listener(video, stoppedAt))
  }
  activeTrailerStop = stop

  /*
    ⚠⚠ MƏNBƏ BOŞ OLSA DA SERVERDƏN SORUŞULUR (sahib tələbi 2026-08-11: «tv
       projelərdə film modal açılanda birbaşa trailer başlasın, posterə gərək
       yoxdur modalda görünsün»).

       Əvvəl boş `asset` DƏRHAL afişaya düşürdü, yəni modal treyleri yalnız DETAL
       cavabı gələndən sonra (`detail.trailerUrl` ilə) başlada bilirdi — həmin
       1-2 saniyədə qutuda afişa dururdu. Kart cavabında treyler sahəsi YOXDUR,
       amma `…/{slug}/trailer` ucu var və detala ehtiyac duymur.
    ⚠ `null` qaytarılanda davranış DƏYİŞMİR: `giveUp()` afişanı qoyur (qırmızı
      xətt 6 — kadr boş qalmır).
  */
  const source = asset ? resolveTrailer(asset) : ({ kind: 'unsupported', reason: '' } as TrailerSource)
  if (!source) {
    onFallback()
    return stop
  }

  video.muted = true
  video.setAttribute('playsinline', '')
  video.setAttribute('preload', 'auto')

  // ⚠ `loadeddata` — `playing` DEYİL (sahib tələbi 2026-08-04: «poster preview
  //   görünür və çox pisdir, birbaşa trailer başlasın»). `playing` yalnız oynatma
  //   HƏQİQƏTƏN başlayanda atılır; ilk kadr ondan əvvəl hazır olur. `playing` də
  //   saxlanılır: bəzi köhnə TV mühərrikləri `loadeddata` atmır.
  video.addEventListener('loadeddata', onLoaded, false)
  video.addEventListener('playing', onPlaying, false)
  video.addEventListener('ended', onEnded, false)
  video.addEventListener('error', onError, false)

  // Sığorta saatı: N saniyəyə ilk kadr gəlmirsə afişa qoyulur (qırmızı xətt 6 —
  // kadr boş qalmır). Treyler sonradan gəlsə onsuz da ÜSTDƏN örtür.
  frameTimer = window.setTimeout(giveUp, 4000)

  const startPlayback = (): void => {
    if (disposed) return
    const started = video.play()
    if (started && started.catch) started.catch(giveUp)
    probeTimer = window.setTimeout(() => {
      probeTimer = 0
      if (!disposed && video.paused) giveUp()
    }, 1500)
  }

  const attach = (url: string): void => {
    if (disposed) return

    const playNative = (): void => {
      video.src = url
      startPlayback()
    }

    // ⚠ SIRA `screens/detail.ts` İLƏ EYNİDİR: `probably` → native · hls.js · native.
    //   Bu, tam oynadıcının sırasından (orada hls.js birincidir) QƏSDƏN fərqlidir:
    //   fon treylerində keyfiyyət/altyazı menyusu YOXDUR, yəni hls.js-in yeganə
    //   faydası itir, əvəzində CDN-dən kitabxana gözləmək ilk kadrı gecikdirər.
    if (canPlayHlsNatively(video)) {
      playNative()
      return
    }
    void loadHlsJs().then((Ctor) => {
      if (disposed) return
      if (!Ctor || !Ctor.isSupported()) {
        playNative()
        return
      }
      // ⚠ Konfiqurasiya tam oynadıcı ilə eynidir: defolt təxmin ~500 kbit/s-dir,
      //   yəni treyler manifestin ƏN AŞAĞI pilləsindən başlayır və qısa olduğu üçün
      //   ABR yuxarı qalxana qədər bitir (2026-08-04 auditinin tapdığı qüsur).
      hls = new Ctor({ abrEwmaDefaultEstimate: 6000000, maxBufferLength: 30 })
      hls.loadSource(url)
      hls.attachMedia(video)
      startPlayback()
    })
  }

  if (source.kind === 'hls') {
    attach(source.url)
  } else {
    // `unsupported` = vimeo → serverdən açıq HLS istənilir (birbaşa embed TV-də oynamır).
    void trailerSourceFor(slug).then((resolved) => {
      if (disposed) return
      if (resolved && resolved.kind === 'hls') attach(resolved.url)
      else giveUp()
    })
  }

  return stop
}

/**
 * Hero modal/kart treyleri üçün dekoderi buraxır, o bağlananda qaldığı yerdən
 * qayıdır. Səhifə söküləndə dinləyici də silinir; gecikmiş bağlanma köhnə
 * heronu başlatmır (2026-10-22, modal aç/bağla regressiyası).
 */
export function startResumableHeroTrailer(
  video: HTMLVideoElement,
  asset: string | null | undefined,
  slug: string,
  onFirstFrame: () => void,
  onFallback: () => void,
): () => void {
  let disposed = false
  let interrupted = false
  let ended = false
  let position = 0
  let resumeTimer = 0
  let stop: (() => void) | null = null
  const seek = (): void => {
    if (position > 0) {
      try { video.currentTime = position } catch { /* Metadata hələ hazır olmaya bilər. */ }
    }
  }
  const onEnded = (): void => { ended = true }
  const resume = (): void => {
    resumeTimer = 0
    if (disposed || ended || !interrupted || activeTrailerStop) return
    interrupted = false
    video.removeEventListener('loadedmetadata', seek)
    video.addEventListener('loadedmetadata', seek)
    stop = startMutedTrailer(video, asset, slug, () => {
      video.removeEventListener('loadedmetadata', seek)
      onFirstFrame()
    }, onFallback)
  }
  const onStop = (stoppedVideo: HTMLVideoElement, time: number): void => {
    if (disposed || ended) return
    if (stoppedVideo === video) {
      position = time
      interrupted = true
    }
    if (interrupted) {
      if (resumeTimer) window.clearTimeout(resumeTimer)
      // Modal dəyişərkən yeni treyler eyni çağırışda başlayır; əvvəl onu gözlə.
      resumeTimer = window.setTimeout(resume, 0)
    }
  }
  video.addEventListener('ended', onEnded)
  trailerStopListeners.push(onStop)
  stop = startMutedTrailer(video, asset, slug, onFirstFrame, onFallback)
  return () => {
    disposed = true
    if (resumeTimer) window.clearTimeout(resumeTimer)
    const index = trailerStopListeners.indexOf(onStop)
    if (index >= 0) trailerStopListeners.splice(index, 1)
    video.removeEventListener('ended', onEnded)
    video.removeEventListener('loadedmetadata', seek)
    if (stop) stop()
  }
}

/**
 * hls.js-i CDN-dən yükləyir — MSE olan HƏR cihazda (real TV daxil) çağırılır.
 *
 * ⚠ ƏSAS BUNDLE-A QOŞULMUR (Shaka ilə eyni səbəb, `lib/shaka.ts`): `vite.config.ts`
 *   hər şeyi tək iife fayla yığır, yəni import edilsəydi kitabxana sadəcə kataloqa
 *   baxanda da parse olunardı.
 * ⚠ CDN AÇILMASA OYNATMA DAYANMIR: `null` qaytarılır və çağıran native `<video>`
 *   yoluna düşür (menyusuz, amma işlək) — bax `screens/player.ts → playHlsUrl`.
 *   Tizen-də `cdn.jsdelivr.net` `packaging/tizen/config.xml` `<access>` siyahısında
 *   olmalıdır, yoxsa sorğu SƏSSİZCƏ bloklanır.
 */
export function loadHlsJs(): Promise<HlsCtor | null> {
  const existing = (window as unknown as { Hls?: HlsCtor }).Hls
  if (existing) return Promise.resolve(existing)
  if (hlsPromise) return hlsPromise

  hlsPromise = new Promise<HlsCtor | null>((resolve) => {
    // ⚠ ÖZ ORIGIN-imiz BİRİNCİDİR, CDN ehtiyatdır (`vite.config.ts → playerAssets`).
    //   Paketlənmiş app-da nisbi ünvan `file://`-dən, veb TV-də `tv.kinobox.az`-dan
    //   gəlir; yəni oynatma xarici bir domenin açılmasından ASILI DEYİL.
    var sources = ['hls.min.js', HLS_CDN]
    var index = 0
    var settled = false

    var finish = function (value: HlsCtor | null): void {
      if (settled) return
      settled = true
      if (!value) hlsPromise = null // növbəti cəhd yenidən yükləməyə çalışsın
      resolve(value)
    }

    var attempt = function (): void {
      var src = sources[index]
      if (!src) {
        finish(null)
        return
      }
      index += 1

      var script = document.createElement('script')
      var timer = window.setTimeout(function () {
        // Cavabsız qalan mənbəni gözləmirik — növbətisinə keçirik.
        script.onload = null
        script.onerror = null
        attempt()
      }, SCRIPT_TIMEOUT_MS)

      script.src = src
      script.onload = function () {
        window.clearTimeout(timer)
        var loaded = (window as unknown as { Hls?: HlsCtor }).Hls
        if (loaded) finish(loaded)
        else attempt()
      }
      script.onerror = function () {
        window.clearTimeout(timer)
        attempt()
      }
      document.head.appendChild(script)
    }

    attempt()
  })
  return hlsPromise
}
